import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { findFirstPartyMcpBySlug } from '@wix/ai-scheduling-assistant-constants';
import { useGenericBi } from './use-generic-bi';
import { useTranslation } from './i18n';
import { useProductName } from './use-brand';
import { usePageLoadBi } from './use-page-load-bi';
import { connectorConnectRoute } from './connector-connect-route';
import {
  authorizeConnector,
  calendarProviderToProviderId,
  connectorDisplayName,
  disconnectCalendarAccounts,
  disconnectConnector,
  fetchConnectors,
  type ConnectorStatus,
} from './connectors';
import { PROVIDERS, useCalendarConnect, type Provider } from '../components/CalendarConnect';
import { chatHandoffLink } from './chat-handoff';
import { deleteMcpConnection, fetchMcpConnections, type McpConnectionSummary } from './mcp';
import { useBasePath } from './use-base-path';

// Shared connectors logic for both brand bodies (components/{orion,symphony}/ConnectorsBody).
// The data layer + connect/disconnect/add-another/poll flow live here so the two brand-pure
// UIs render the same behavior — only the presentation forks. See the page CLAUDE.md rule.

export const CONNECTORS_PAGE = { page_name: 'connectors' as const, page_version: 'v1_connectors' as const };

// ===== Apps (Composio + native calendar) =====

export type AppsState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; enabled: boolean; connectors: ConnectorStatus[]; multiAccountEnabled: boolean };

/**
 * The curated Connectors row order, ranked by how many accounts actually have each connector CONNECTED:
 * distinct metasites per toolkit in the server's `connectors-status` log, 13-27 Aug 2026 (2,990 accounts
 * opened the screen, 1,670 of them had at least one connection). A toolkit not named here follows
 * alphabetically, so whatever Composio's catalog gains tomorrow still renders - just after the measured ones.
 *
 * It replaces a hand-picked list of 10 that had drifted badly from what accounts do: Instagram (3rd by
 * accounts) and Facebook (4th) were unranked and sat in the alphabetical mush behind ActiveCampaign and
 * Airtable; Slack was ranked 9th and is 29th; and one rank slot went to `googlecalendar`, a row the server
 * drops UNCONDITIONALLY (`dropUnavailableComposioRows`) and which therefore could never appear at all.
 *
 * Two deliberate exceptions to the measured ranking, each of which would otherwise read as a bug:
 * - The three native (Nylas) `calendar-*` rows stay ADJACENT, under Google Calendar's own rank. They are one
 *   feature split by provider, and dropping Apple Calendar (9 accounts) to the end of the list is precisely
 *   the defect `ConnectorsOrderTests` was written for.
 * - `canva` sits between Google Meet and Zoom by product decision, above its measured rank. Note it is a
 *   DEPRIORITIZED slug (`isDeprioritizedConnectorSlug`): the server drops its row unless the account already
 *   connected it, so this placement is only ever visible to those accounts.
 *
 * Mirrored 1:1 on both phones (`ConnectorsScreen.swift`, `ConnectorsScreen.kt`) - keep the three identical;
 * `test/connectors-order.test.ts`, `ConnectorsOrderTests` and `ConnectorsOrderTest` pin them. Re-cutting this
 * costs an app release on the phones, which is the argument for the follow-up: a `rank` on the status DTO fed
 * by a databag list, so reordering becomes a config change on the server instead of three code edits.
 */
const CONNECTOR_ORDER = [
  'gmail',
  'calendar-google',
  'calendar-microsoft',
  'calendar-apple',
  'instagram',
  'facebook',
  'linkedin',
  'googledrive',
  'googlesheets',
  'youtube',
  'googledocs',
  'google_analytics',
  'google_search_console',
  'google_maps',
  'outlook',
  'notion',
  'googlemeet',
  'canva',
  'zoom',
  'github',
  'calendly',
  'googleads',
  'googletasks',
  'excel',
  'square',
  'stripe',
  'hubspot',
  'mailchimp',
  'slack',
  'googleslides',
  'one_drive',
  'dropbox',
  'quickbooks',
  'reddit',
  'figma',
  'discord',
  'microsoft_teams',
  'airtable',
  'clickup',
  'asana',
  'pinterest',
  'cal',
  'supabase',
  'apollo',
  'todoist',
  'trello',
  'discordbot',
  'linear',
  'reddit_ads',
  'typeform',
  'shopify',
  'pipedrive',
  'active_campaign',
  'perplexityai',
  'box',
  'eventbrite',
  'share_point',
  'jira',
  'salesforce',
  'postiz_mcp',
  'intercom',
];
const orderRank = (toolkit: string) => {
  const i = CONNECTOR_ORDER.indexOf(toolkit);
  return i === -1 ? CONNECTOR_ORDER.length : i;
};
export function sortConnectors(list: ConnectorStatus[]): ConnectorStatus[] {
  return [...list].sort(
    (a, b) =>
      Number(b.connected) - Number(a.connected) ||
      orderRank(a.toolkit) - orderRank(b.toolkit) ||
      connectorDisplayName(a).localeCompare(connectorDisplayName(b)),
  );
}

/**
 * The registry slugs the apps section is actually rendering a row for. The MCP section hides exactly
 * these connections and nothing more, so a registry server is never on the screen twice and never absent
 * from both places.
 *
 * Per-slug rather than the all-or-nothing rule it replaces, because a gated connector's row disappears
 * the moment its rollout closes while the CONNECTION lives on. Under the old rule one ungated row
 * (WordPress) was enough to hide EVERY registry connection — so a user who connected TikTok during a
 * pilot and was later dropped from the allowlist lost the connector row AND the MCP row, leaving no way
 * to disconnect it on web at all. The server deliberately keeps disconnect ungated for exactly that case;
 * this is the client half of the same promise. It also still covers the original reason: on a catalog
 * read failure or an unconfigured Composio key the apps section renders no rows, so this set is empty and
 * every connection shows here.
 *
 * `null` means "not answerable yet", which is NOT the same as the empty set and must not collapse into it:
 * the two controllers fetch independently, and the MCP read routinely lands first (the apps read carries
 * Composio's 4s timeout and its 13-21s tail). Treating loading as "the apps section claims nothing" renders
 * a connected server as a raw MCP row for that whole window and then yanks it once its card arrives.
 */
export function connectorRowSlugs(state: AppsState): Set<string> | null {
  if (state.kind === 'loading') {
    return null;
  }
  if (state.kind !== 'ready') {
    return new Set();
  }
  return new Set(state.connectors.filter((connector) => connector.provider === 'mcp').map((connector) => connector.toolkit));
}

/** Whether the MCP section shows this connection: everything that isn't a registry server, plus the registry
 *  servers the apps section is not rendering a row for. Held back entirely while that answer is pending. */
export function mcpSectionShowsConnection(entry: { slug: string } | undefined, rowedSlugs: Set<string> | null): boolean {
  if (!entry) {
    return true;
  }
  return rowedSlugs !== null && !rowedSlugs.has(entry.slug);
}

// ACTIVE accounts on a row — drives add-another success detection (count rising over the pre-popup
// baseline, covering both first connect 0→1 and add-another 1→2). Falls back to the connected boolean.
export function accountCountFor(connector: ConnectorStatus | undefined): number {
  return connector?.accounts?.length ?? (connector?.connected ? 1 : 0);
}

// Composio lands its OAuth callback on its own servers — the popup never
// messages back — so polling status is the only signal a connect completed.
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 80;

export interface AppsController {
  state: AppsState;
  busy: string | null;
  actionError: string | null;
  // Apple connects with an app-specific password (not OAuth) — its card expands an inline form.
  icloudOpenFor: string | null;
  appleId: string;
  applePassword: string;
  setAppleId: (v: string) => void;
  setApplePassword: (v: string) => void;
  submitICloud: () => void;
  connect: (connector: ConnectorStatus) => void;
  addAnother: (connector: ConnectorStatus) => void;
  disconnect: (connector: ConnectorStatus, name: string, accountId?: string) => void;
  /** Re-read `/api/connectors/status`. Exposed so a caller can re-sync on its own signal — the home,
   *  kept alive by DashboardHost, calls it on return since it never remounts to re-run the load. */
  refresh: () => Promise<void>;
}

export function useAppsController(): AppsController {
  const productName = useProductName();
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [state, setState] = useState<AppsState>({ kind: 'loading' });
  // Fire the connectors page-view (generic loadFinish, evid 5) once apps have loaded, tagged with
  // which toolkits came back and which were already connected — so the page-view captures load state.
  usePageLoadBi(CONNECTORS_PAGE, {
    enabled: state.kind === 'ready',
    trackBack: true,
    additionalFields:
      state.kind === 'ready'
        ? {
            loaded_connectors: state.connectors.map((c) => c.toolkit).join(','),
            connected_connectors: state.connectors
              .filter((c) => c.connected)
              .map((c) => c.toolkit)
              .join(','),
            connected_count: String(state.connectors.filter((c) => c.connected).length),
          }
        : undefined,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);
  // Native calendar rows reuse the settings/onboarding calendar plumbing: OAuth popup for
  // Google/Outlook, the inline app-password form for Apple.
  const { connectOAuth, connectICloud } = useCalendarConnect();
  const [icloudOpenFor, setIcloudOpenFor] = useState<string | null>(null);
  const [appleId, setAppleId] = useState('');
  const [applePassword, setApplePassword] = useState('');
  const prevConnectedRef = useRef<Set<string> | null>(null);
  // ACTIVE account count per toolkit from the last read — the baseline an add-another flow compares
  // against to know when a NEW account landed (covers 0→1 and 1→2 alike).
  const accountCountsRef = useRef<Map<string, number>>(new Map());
  // First-party MCP rows (WordPress) connect via a chat handoff (the assistant runs the OAuth dance
  // through ManageMcp) — needs the router + mount prefix to build the handoff link imperatively.
  const router = useRouter();
  const basePath = useBasePath();

  const applyConnectorStatus = useCallback(
    (connectors: ConnectorStatus[]) => {
      const nowConnected = new Set(connectors.filter((c) => c.connected).map((c) => c.toolkit));
      const prevConnected = prevConnectedRef.current;
      if (prevConnected) {
        nowConnected.forEach((toolkit) => {
          if (!prevConnected.has(toolkit)) {
            logPageClickBi({ ...CONNECTORS_PAGE, button_name: `connect_${toolkit}`, action: 'connected' });
          }
        });
      }
      prevConnectedRef.current = nowConnected;
      accountCountsRef.current = new Map(connectors.map((c) => [c.toolkit, accountCountFor(c)]));
    },
    [logPageClickBi],
  );

  const refresh = useCallback(async () => {
    try {
      const { enabled, connectors, multiAccountEnabled } = await fetchConnectors();
      applyConnectorStatus(connectors);
      setState({ kind: 'ready', enabled, connectors, multiAccountEnabled });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, [applyConnectorStatus]);

  // Both effects reach `refresh` through a ref so neither re-runs when its identity moves. Depending on
  // it directly is what made the initial load self-perpetuating: refresh → setState → render → a new
  // `refresh` (it transitively depended on a BI logger that was rebuilt every render) → the effect fires
  // again. That was ~33 reads/sec of /api/connectors/status for as long as the page stayed open, which
  // starved the voice call's WebSocket handshake. The dependency is stable again now; this makes the
  // load-on-mount structurally incapable of flooding even if some future dependency churns.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    void refreshRef.current();
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      popupRef.current?.close();
    };
  }, []);

  // Resolve once the toolkit's ACTIVE account count rises above `baseline` — works for a first connect
  // (0→1) and for adding another account (1→2) alike.
  const pollUntilAccountAdded = useCallback((toolkit: string, baseline: number, popup: Window) => {
    return new Promise<void>((resolve, reject) => {
      let attempts = 0;
      let inFlight = false;
      const timer = setInterval(async () => {
        if (popup.closed) {
          clearInterval(timer);
          pollTimerRef.current = null;
          reject(new Error('popup_closed'));
          return;
        }
        if (inFlight) return;
        attempts += 1;
        if (attempts > MAX_POLL_ATTEMPTS) {
          clearInterval(timer);
          pollTimerRef.current = null;
          resolve();
          return;
        }
        inFlight = true;
        try {
          const { connectors } = await fetchConnectors();
          if (accountCountFor(connectors.find((c) => c.toolkit === toolkit)) > baseline) {
            clearInterval(timer);
            pollTimerRef.current = null;
            resolve();
          }
        } catch {
          // transient read failure — keep polling within the cap
        } finally {
          inFlight = false;
        }
      }, POLL_INTERVAL_MS);
      pollTimerRef.current = timer;
    });
  }, []);

  // Native calendar connect: OAuth popup (Google/Outlook) returns to chat.nownia.com which postMessages
  // success; Apple expands an inline app-password form instead. Refreshes the list on success.
  const connectNative = useCallback(
    async (connector: ConnectorStatus) => {
      if (busy) return;
      const providerId = calendarProviderToProviderId(connector.calendarProvider);
      if (providerId === 'apple') {
        setActionError(null);
        setIcloudOpenFor((open) => (open === connector.toolkit ? null : connector.toolkit));
        return;
      }
      const provider = PROVIDERS.find((p): p is Provider => p.id === providerId);
      if (!provider || provider.kind !== 'oauth') {
        setActionError("Couldn't start the calendar connect. Please try again.");
        return;
      }
      setIcloudOpenFor(null); // close a lingering Apple form when connecting a different provider
      setBusy(connector.toolkit);
      setActionError(null);
      try {
        if (await connectOAuth(provider)) await refresh();
      } catch {
        setActionError("Couldn't reach the calendar service. Please try again.");
      } finally {
        setBusy(null);
      }
    },
    [busy, connectOAuth, refresh],
  );

  const submitICloud = useCallback(async () => {
    if (busy) return;
    setBusy('calendar-apple');
    setActionError(null);
    try {
      if (await connectICloud(appleId, applePassword)) {
        setIcloudOpenFor(null);
        setAppleId('');
        setApplePassword('');
        await refresh();
      }
    } catch {
      setActionError("Couldn't connect that Apple account. Please try again.");
    } finally {
      setBusy(null);
    }
  }, [busy, connectICloud, appleId, applePassword, refresh]);

  // Composio connect — first connect, or "add another account" (allowMultiple). Opens the OAuth popup
  // and polls until the toolkit's account count rises over its pre-popup baseline.
  const connectComposio = useCallback(
    async (toolkit: string, allowMultiple = false) => {
      if (busy) return;
      setIcloudOpenFor(null); // close a lingering Apple form when connecting a Composio app
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `${allowMultiple ? 'add_another' : 'connect'}_${toolkit}` });
      setBusy(toolkit);
      setActionError(null);
      const baseline = accountCountsRef.current.get(toolkit) ?? 0;
      let popup: Window | null = null;
      try {
        const result = await authorizeConnector(toolkit, allowMultiple);
        if (result.alreadyConnected) {
          await refresh();
          return;
        }
        if (!result.url) {
          throw new Error(result.error || 'no_auth_url');
        }
        popup = window.open(result.url, 'orion-connector-oauth', 'width=520,height=720');
        popupRef.current = popup;
        if (!popup) {
          throw new Error('popup_blocked');
        }
        await pollUntilAccountAdded(toolkit, baseline, popup);
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message === 'popup_blocked') {
          setActionError("Couldn't open the connect window. Check your browser's popup blocker.");
        } else if (message === 'popup_closed') {
          await refresh();
        } else {
          setActionError("Couldn't reach the connector service. Please try again.");
        }
      } finally {
        popup?.close();
        popupRef.current = null;
        setBusy(null);
      }
    },
    [busy, logPageClickBi, pollUntilAccountAdded, refresh],
  );

  // First-party MCP connect: no popup/poll — the OAuth dance happens in chat (ManageMcp hands the
  // user an authorize link), so we drop them into a fresh thread with the registry's pre-filled
  // prompt naming the server and its canonical URL.
  //
  // Also the destination for any row the server sent `connectVia: 'assistant-chat'` on, whatever its
  // provider. Such a row need not be in the registry, so the prompt falls back to naming the connector —
  // the same sentence the phones build. The missing-entry refusal still stands for a row routed here by
  // its PROVIDER, where an unresolvable slug really is a broken row rather than a rail we were told about.
  const connectMcp = useCallback(
    (connector: ConnectorStatus) => {
      if (busy) return;
      const firstParty = findFirstPartyMcpBySlug(connector.toolkit);
      if (!firstParty && connector.connectVia !== 'assistant-chat') {
        setActionError("Couldn't start the connect. Please try again.");
        return;
      }
      const connectPrompt = firstParty?.connectPrompt ?? `I want to connect ${connector.name || connector.toolkit}.`;
      setIcloudOpenFor(null); // close a lingering Apple form when connecting an MCP server
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `connect_${connector.toolkit}` });
      const handoff = chatHandoffLink({ kind: 'new', message: connectPrompt }, basePath);
      void router.push(handoff.route, handoff.path);
    },
    [basePath, busy, logPageClickBi, router],
  );

  const connect = useCallback(
    (connector: ConnectorStatus) =>
      connectorConnectRoute(connector) === 'assistant-chat'
        ? void connectMcp(connector)
        : connectorConnectRoute(connector) === 'native-calendar'
          ? void connectNative(connector)
          : void connectComposio(connector.toolkit),
    [connectNative, connectMcp, connectComposio],
  );

  // Link an additional account on an already-connected row. Native (calendar) rows re-run the native
  // connect flow (which already supports several calendars); Composio rows mint another link with allowMultiple.
  const addAnother = useCallback(
    (connector: ConnectorStatus) =>
      connectorConnectRoute(connector) === 'assistant-chat'
        ? void connectMcp(connector)
        : connectorConnectRoute(connector) === 'native-calendar'
          ? void connectNative(connector)
          : void connectComposio(connector.toolkit, true),
    [connectNative, connectMcp, connectComposio],
  );

  // Disconnect a whole row (no accountId) or a single account of a multiply-linked row. Native rows
  // delete the backing calendar token(s); Composio rows revoke the connected account(s).
  const disconnect = useCallback(
    async (connector: ConnectorStatus, name: string, accountId?: string) => {
      if (busy) return;
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `disconnect_${connector.toolkit}` });
      // The routines warning rides the WHOLE-connector disconnect only. Removing one account of a
      // multiply-linked row leaves the connector connected, so nothing is deleted and saying otherwise
      // would talk someone out of an action that costs them nothing.
      //
      // Two whole sentences rather than a base string plus an appended clause: a clause bolted onto a
      // translated sentence lands in the wrong place in most languages, and several cannot take it at
      // all without recasting the verb.
      const removesConnector = !accountId || (connector.accounts ?? []).length <= 1;
      const confirmKey = removesConnector ? 'connectors.disconnect.confirmWithRoutines' : 'connectors.disconnect.confirm';
      if (!window.confirm(t(confirmKey, { name, productName }))) return;
      setBusy(accountId ?? connector.toolkit);
      setActionError(null);
      try {
        if (connector.provider === 'native') {
          const ids = accountId ? [accountId] : (connector.accounts ?? []).map((account) => account.id);
          await disconnectCalendarAccounts(ids);
        } else if (connector.provider === 'mcp') {
          // MCP rows carry the McpConnectionService connection ids as their accounts.
          const ids = accountId ? [accountId] : (connector.accounts ?? []).map((account) => account.id);
          for (const id of ids) {
            await deleteMcpConnection(id);
          }
        } else if (connector.provider === 'wix-service') {
          await disconnectConnector(connector.toolkit);
        } else {
          await disconnectConnector(connector.toolkit, accountId);
        }
        await refresh();
      } catch {
        setActionError(`Couldn't disconnect ${name}. Please try again.`);
        // A row can hold several accounts and they're removed one at a time, so a mid-way failure
        // leaves some already gone — re-read rather than keep rendering rows that no longer exist.
        await refresh();
      } finally {
        setBusy(null);
      }
    },
    [busy, logPageClickBi, refresh, t, productName],
  );

  return {
    state,
    busy,
    actionError,
    icloudOpenFor,
    appleId,
    applePassword,
    setAppleId,
    setApplePassword,
    submitICloud,
    connect,
    addAnother,
    disconnect,
    refresh,
  };
}

// ===== MCP servers =====

export type McpState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; connections: McpConnectionSummary[] };

export const MCP_CONNECT_PROMPT = 'I want to connect a new MCP server.';

export interface McpController {
  state: McpState;
  busy: string | null;
  actionError: string | null;
  remove: (id: string, name: string) => void;
  logConnectClick: () => void;
}

export function useMcpController(): McpController {
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const [state, setState] = useState<McpState>({ kind: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      // Every connection, registry-backed or not. Which of them this section actually renders is a
      // render-time call (`connectorRowSlugs`) rather than a fetch-time one, so the answer
      // changing as the apps section settles never costs a second request.
      const connections = await fetchMcpConnections();
      setState({ kind: 'ready', connections });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  // Same ref indirection as the apps controller above. This `refresh` has no dependencies today, so the
  // effects fire once either way — but the flood that broke voice calls was one added dependency away
  // from happening here too, and the mount fetch has no reason to be coupled to refresh's identity.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    void refreshRef.current();
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const remove = useCallback(
    async (id: string, name: string) => {
      if (busy) return;
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'disconnect_mcp' });
      if (!window.confirm(`Remove ${name}? ${productName} will lose access to its tools.`)) return;
      setBusy(id);
      setActionError(null);
      try {
        await deleteMcpConnection(id);
        await refresh();
      } catch {
        setActionError(`Couldn't remove ${name}. Please try again.`);
      } finally {
        setBusy(null);
      }
    },
    [busy, logPageClickBi, refresh],
  );

  const logConnectClick = useCallback(() => logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'connect_mcp' }), [logPageClickBi]);

  return { state, busy, actionError, remove, logConnectClick };
}
